import { Close } from '@highmobility/console-ui-components';
import { generatePath, Link } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getOemDisplayName, groupOemsForDisplay } from '@/utils/oem-display-name';

import {
  fetchProjects,
  fetchVehicleClearanceOems,
  fetchVehicleClearances,
} from '@/services/apiService';
import { ROUTE_PATHS } from '@/router/routes';
import { useTypedParams } from '@/hooks/useTypedParams';
import {
  VehicleClearanceFilterBar,
  VehicleClearanceFilters,
} from '@/components/organisms/VehicleClearanceFilterBar';
import { VehicleClearanceStatusModal } from '@/components/organisms/VehicleClearanceStatusModal';
import { VehicleClearanceTable } from '@/components/organisms/VehicleClearanceTable';

export const VehicleClearanceView = () => {
  const { teamId, projectId } = useTypedParams<typeof ROUTE_PATHS.VEHICLE_CLEARANCE>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<{
    id: string;
    vin: string;
    currentStatus: string;
    oemName: string;
  } | null>(null);

  const [activeFilters, setActiveFilters] = useState<Record<string, string | undefined>>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data: projectsResponse } = useQuery({
    queryKey: ['projects', teamId],
    queryFn: () => fetchProjects(teamId),
  });

  const { data: oemsResponse } = useQuery({
    queryKey: ['vehicle-clearance-oems', teamId, projectId],
    queryFn: () => fetchVehicleClearanceOems(teamId, projectId),
  });

  const availableOems = useMemo(() => {
    const oems = Array.isArray(oemsResponse?.data) ? oemsResponse.data : [];
    return oems.map((oem: any) => ({ ...oem, name: getOemDisplayName(oem.name) }));
  }, [oemsResponse?.data]);

  const groupedOems = useMemo(() => {
    const oems = Array.isArray(oemsResponse?.data) ? oemsResponse.data : [];
    return groupOemsForDisplay(oems);
  }, [oemsResponse?.data]);

  const availableProjects = useMemo(() => {
    return (projectsResponse?.data || []).map((project) => ({
      id: project.id,
      name: project.name,
    }));
  }, [projectsResponse?.data]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicle-clearances', teamId, projectId, activeFilters, page, pageSize],
    queryFn: () =>
      fetchVehicleClearances(teamId, projectId, {
        ...activeFilters,
        page,
        pageSize,
      } as any),
  });

  const responseBody = data?.data as any;
  const clearances = Array.isArray(responseBody?.data) ? responseBody.data : [];
  const totalCount =
    responseBody?.meta?.totalCount || responseBody?.totalCount || clearances.length;

  const handleSearch = useCallback((uiFilters: VehicleClearanceFilters) => {
    const mapStatusForDb = (s: string) =>
      s.toLowerCase() === 'pending' ? 'created' : s.toLowerCase();

    const apiQuery = {
      projectIds: uiFilters.projectIds.length > 0 ? uiFilters.projectIds.join(',') : undefined,
      vins: uiFilters.vins.length > 0 ? uiFilters.vins.join(',') : undefined,
      statuses:
        uiFilters.statuses.length > 0
          ? uiFilters.statuses.map(mapStatusForDb).join(',')
          : undefined,
      oems: uiFilters.oems.length > 0 ? uiFilters.oems.join(',') : undefined,
      from: uiFilters.dateRange.from ? uiFilters.dateRange.from.toISOString() : undefined,
      to: uiFilters.dateRange.to ? uiFilters.dateRange.to.toISOString() : undefined,
    };

    setActiveFilters((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(apiQuery)) {
        return prev;
      }
      return apiQuery;
    });

    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setActiveFilters(undefined);
    setPage(1);
  }, []);

  const handleRowClick = (vin: string) => {
    const selected = clearances.find((c: any) => c.vin === vin);

    if (selected) {
      const formatStatus = (dbStatus: string) => {
        if (dbStatus === 'created') return 'Pending';
        return dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1);
      };
      const matchedOem = availableOems.find((o: any) => o.id === selected.oem);

      setSelectedDetails({
        id: selected.clearance_id,
        vin: selected.vin,
        currentStatus: formatStatus(selected.status),
        oemName: matchedOem ? matchedOem.name : `Unknown (${selected.oem})`,
      });

      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDetails(null);
  };

  const formatStatus = (dbStatus: string) => {
    if (dbStatus === 'created') return 'Pending';
    return dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1);
  };

  const tableData = clearances.map((c: any): any => {
    const matchedOem = availableOems.find((o: any) => o.id === c.oem);

    return {
      clearanceId: c.id,
      applicationId: c.applicationId,
      vin: c.vin,
      oem: matchedOem ? matchedOem.name : `Unknown (${c.oem})`,
      status: formatStatus(c.status),
      insertedAt: c.insertedAt,
      stateChangedAt: c.stateChangedAt,
    };
  });

  return (
    <main className="bg-dark-green flex h-full w-full flex-col text-white">
      <div className="flex items-center justify-between border-b border-black-20 bg-[#2B4247] px-6 py-4">
        <h5 className="text-heading-5">Vehicle Clearances</h5>
        <Link to={generatePath(ROUTE_PATHS.PROJECT, { teamId, projectId })}>
          <Close className="h-4 w-4 cursor-pointer text-white-40 transition-colors hover:text-white" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-hidden p-6">
        <VehicleClearanceFilterBar
          currentProjectId={projectId}
          onSearch={handleSearch}
          onReset={handleReset}
          projects={availableProjects}
          availableOems={groupedOems}
        />

        {error && <p className="text-red-500">Failed to load data. Please try again.</p>}

        {!error && (
          <VehicleClearanceTable
            data={tableData}
            onRowClick={handleRowClick}
            isLoading={isLoading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      <VehicleClearanceStatusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        details={selectedDetails}
      />
    </main>
  );
};
